import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  TextField,
  Avatar,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Edit,
  Save,
  Cancel,
  Person,
  Email,
  Business,
  Security,
  Assignment,
  Construction,
  CheckCircle,
  Pending,
  Warning,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { rentalAPI } from '../../services/api';
import { Rental } from '../../types/rental';

const getAvatarText = (fullName?: string | null, username?: string | null) => {
  const name = (fullName || '').trim();
  if (name) {
    const ch = name[0];
    return ch.toUpperCase ? ch.toUpperCase() : ch;
  }
  const user = (username || '').trim();
  if (user) {
    let firstNonDigit = '' as string;
    for (let i = 0; i < user.length; i++) {
      const c = user.charAt(i);
      if (!(c >= '0' && c <= '9')) { firstNonDigit = c; break; }
    }
    if (!firstNonDigit) firstNonDigit = user.charAt(0);
    return (firstNonDigit && firstNonDigit.toUpperCase) ? firstNonDigit.toUpperCase() : (firstNonDigit || 'U');
  }
  return 'U';
};

const formatDateOnly = (value?: string | null) => {
  if (!value) return '—';
  const s = String(value);
  if (s.includes('T')) return s.split('T')[0];
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  return isNaN(d.getTime()) ? s : d.toISOString().slice(0, 10);
};

const ProfilePage: React.FC = () => {
  const { user, token } = useAuth();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userRentals, setUserRentals] = useState<Rental[]>([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    department: user?.department_name || '',
  });

  useEffect(() => {
    if (user) {
      fetchUserRentals();
    }
  }, [user]);

  const fetchUserRentals = async () => {
    try {
      setLoading(true);
      const response = await rentalAPI.getRentals({ user_id: user?.id });
      setUserRentals(response.results);
    } catch (error) {
      showSnackbar('載入租借記錄失敗', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleEdit = () => {
    setEditing(true);
    setFormData({
      name: user?.name || '',
      email: user?.email || '',
      department: user?.department_name || '',
    });
  };

  const handleCancel = () => {
    setEditing(false);
    setFormData({
      name: user?.name || '',
      email: user?.email || '',
      department: user?.department_name || '',
    });
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      // 這裡應該調用更新用戶資料的API
      // await userAPI.updateProfile(formData);
      showSnackbar('個人資料更新成功', 'success');
      setEditing(false);
    } catch (error) {
      showSnackbar('更新失敗', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'reserved': return 'info';
      case 'ready': return 'warning';
      case 'active': return 'success';
      case 'completed': return 'default';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'reserved': return '預約中';
      case 'ready': return '準備就緒';
      case 'active': return '使用中';
      case 'completed': return '已完成';
      case 'cancelled': return '已取消';
      default: return status;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'reserved': return <Pending color="info" />;
      case 'ready': return <CheckCircle color="warning" />;
      case 'active': return <CheckCircle color="success" />;
      case 'completed': return <CheckCircle color="inherit" />;
      case 'cancelled': return <Warning color="error" />;
      default: return <Assignment />;
    }
  };

  const activeRentals = userRentals.filter((r: Rental) => r.status === 'active').length;
  const reservedRentals = userRentals.filter((r: Rental) => r.status === 'reserved').length;
  const completedRentals = userRentals.filter((r: Rental) => r.status === 'completed').length;

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
        個人資料
      </Typography>

      <Box display="flex" flexWrap="wrap" gap={3}>
        {/* 基本資料卡片 */}
        <Box width={{ xs: '100%', md: 'calc(33.333% - 16px)' }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'primary.main',
                    fontSize: '2rem',
                    mb: 2,
                  }}
                >
                  {getAvatarText(user?.name as any, user?.username as any)}
                </Avatar>
                <Typography variant="h5" gutterBottom>
                  {user?.name || '未知用戶'}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <Person color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary="用戶名"
                    secondary={user?.username || 'N/A'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Email color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary="電子郵件"
                    secondary={user?.email || 'N/A'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Business color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary="部門"
                    secondary={user?.department_name || 'N/A'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Security color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary="帳號狀態"
                    secondary={user?.is_active ? '啟用' : '停用'}
                  />
                </ListItem>
              </List>
            </CardContent>
            <CardActions>
              <Button
                fullWidth
                variant="contained"
                startIcon={<Edit />}
                onClick={handleEdit}
                disabled={editing}
              >
                編輯資料
              </Button>
            </CardActions>
          </Card>
        </Box>

        {/* 租借統計 */}
        <Box width={{ xs: '100%', md: 'calc(66.667% - 16px)' }}>
          <Box display="flex" flexWrap="wrap" gap={3}>
            {/* 統計卡片 */}
            <Box width={{ xs: '100%', sm: 'calc(33.333% - 16px)' }}>
              <Card sx={{ background: 'linear-gradient(45deg, #4caf50 30%, #81c784 90%)' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="white" gutterBottom variant="h6">
                        使用中
                      </Typography>
                      <Typography variant="h3" color="white" sx={{ fontWeight: 'bold' }}>
                        {activeRentals}
                      </Typography>
                    </Box>
                    <CheckCircle sx={{ fontSize: 40, color: 'white' }} />
                  </Box>
                </CardContent>
              </Card>
            </Box>
            <Box width={{ xs: '100%', sm: 'calc(33.333% - 16px)' }}>
              <Card sx={{ background: 'linear-gradient(45deg, #ff9800 30%, #ffb74d 90%)' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="white" gutterBottom variant="h6">
                        預約中
                      </Typography>
                      <Typography variant="h3" color="white" sx={{ fontWeight: 'bold' }}>
                        {reservedRentals}
                      </Typography>
                    </Box>
                    <Pending sx={{ fontSize: 40, color: 'white' }} />
                  </Box>
                </CardContent>
              </Card>
            </Box>
            <Box width={{ xs: '100%', sm: 'calc(33.333% - 16px)' }}>
              <Card sx={{ background: 'linear-gradient(45deg, #2196f3 30%, #64b5f6 90%)' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="white" gutterBottom variant="h6">
                        已完成
                      </Typography>
                      <Typography variant="h3" color="white" sx={{ fontWeight: 'bold' }}>
                        {completedRentals}
                      </Typography>
                    </Box>
                    <Assignment sx={{ fontSize: 40, color: 'white' }} />
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {/* 最近租借記錄 */}
            <Box width="100%">
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                    最近租借記錄
                  </Typography>
                  {loading ? (
                    <Box display="flex" justifyContent="center" p={2}>
                      <CircularProgress />
                    </Box>
                  ) : userRentals.length === 0 ? (
                    <Typography color="text.secondary" textAlign="center" py={2}>
                      暫無租借記錄
                    </Typography>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>機具</TableCell>
                            <TableCell>租借日期</TableCell>
                            <TableCell>歸還日期</TableCell>
                            <TableCell>狀態</TableCell>
                            <TableCell>用途</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {userRentals.slice(0, 5).map((rental: Rental) => (
                            <TableRow key={rental.id}>
                              <TableCell>
                                <Box display="flex" alignItems="center">
                                  <Construction color="primary" sx={{ mr: 1 }} />
                                  <Box>
                                    <Typography variant="subtitle2">
                                      {rental.equipment?.name || '未知機具'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      {rental.equipment?.equipment_code || 'N/A'}
                                    </Typography>
                                  </Box>
                                </Box>
                              </TableCell>
                            <TableCell>{formatDateOnly(rental.rental_date as any)}</TableCell>
                            <TableCell>{formatDateOnly(rental.return_date as any)}</TableCell>
                              <TableCell>
                                <Chip
                                  icon={getStatusIcon(rental.status)}
                                  label={getStatusText(rental.status)}
                                  color={getStatusColor(rental.status) as any}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" noWrap>
                                  {rental.purpose}
      </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* 編輯個人資料對話框 */}
      <Dialog open={editing} onClose={handleCancel} maxWidth="sm" fullWidth>
        <DialogTitle>編輯個人資料</DialogTitle>
        <DialogContent>
          <Box display="flex" flexWrap="wrap" gap={2} sx={{ mt: 1 }}>
            <Box width="100%">
              <TextField
                fullWidth
                label="姓名"
                value={formData.name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData((prev: any) => ({ ...prev, name: e.target.value }))}
                required
              />
            </Box>
            <Box width="100%">
              <TextField
                fullWidth
                label="電子郵件"
                type="email"
                value={formData.email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData((prev: any) => ({ ...prev, email: e.target.value }))}
                required
              />
            </Box>
            <Box width="100%">
              <TextField
                fullWidth
                label="部門"
                value={formData.department}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData((prev: any) => ({ ...prev, department: e.target.value }))}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} disabled={loading}>
            取消
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            startIcon={<Save />}
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : '儲存'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 通知訊息 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev: any) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((prev: any) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProfilePage;
